import { filter, set } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Divider,
  Paper
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import SearchNotFound from '../components/SearchNotFound';
import { MembersListHead, MembersListToolbar, MembersMoreMenu } from '../sections/reports';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'username', label: 'User Name', alignRight: false },
  { id: 'job', label: 'Job', alignRight: false },
  { id: 'subjob', label: 'Sub Job', alignRight: false },
  { id: 'address', label: 'Address', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'pin', label: 'Pin', alignRight: false },
  { id: 'fromyear', label: 'From Year', alignRight: false },
  { id: 'toyear', label: 'To Year', alignRight: false },
  { id: 'position', label: 'Position', alignRight: false },
  { id: 'image', label: 'Image', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, roleFilter) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filteredArray = stabilizedThis.map((el) => el[0]);

  // Apply text query filter
  if (query) {
    filteredArray = filteredArray.filter((member) => {
      if (String(member.fromyear).toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      if (String(member.toyear).toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      if (member.description && member.description.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      if (member.images && member.images.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      return false;
    });
  }

  // Apply role filter
  if (roleFilter) {
    filteredArray = filteredArray.filter((member) => member.role.toLowerCase() === roleFilter.toLowerCase());
  }

  return filteredArray;
}

export default function Member() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('email'); // Default sort by email
  const [filterName, setFilterName] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // State for role filter
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // State for member
  const [member, setMember] = useState([]);
  const [editingmemberId, setEditingMemberId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [username, setUserName] = useState('');
  const [job, setJob] = useState('');
  const [subjob, setSubJob] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pin, setPin] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [position, setPosition] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const [selectedmember, setSelectedMember] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Fetch member from the server on component mount
  useEffect(() => {
    fetchmember();
  }, []);



  // Fetch member from the server
  const fetchmember = async () => {
    try {
      const response = await axios.get('https://cpccbe.org/api/member/getallmembers');
      setMember(response.data);
    } catch (error) {
      console.error('Error fetching member:', error);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = member.map((n) => n.email); // Use email as unique identifier
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, email) => {
    const selectedIndex = selected.indexOf(email);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, email);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  // Handle opening and closing of modal for editing member
  const handleOpenModal = (memberId) => {
    if (memberId) {
      const membertoEdit = member.find((member) => member._id === memberId);
      if (membertoEdit) {
        setUserName(membertoEdit.username);
        setJob(membertoEdit.job);
        setSubJob(membertoEdit.subjob);
        setAddress(membertoEdit.address);
        setCity(membertoEdit.city);
        setPin(membertoEdit.pin);
        setFromYear(membertoEdit.fromYear);
        setToYear(membertoEdit.toYear);
        setPosition(membertoEdit.position);
        setImageUrl(membertoEdit.imageUrl);

        setEditingMemberId(memberId);
      }
    } else {
      setUserName('');
      setJob('');
      setSubJob('');
      setAddress('');
      setCity('');
      setPin('');
      setFromYear('');
      setToYear('');
      setPosition('');
      setImageUrl('');
      setEditingMemberId(null);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setUserName('');
    setJob('');
    setSubJob('');
    setAddress('');
    setCity('');
    setPin('');
    setFromYear('');
    setToYear('');
    setPosition('');
    setImageUrl('');
    setEditingMemberId(null);
  };

  // Handle adding a new member
  const handleAddmember = async () => {
    try {
      const response = await axios.post('https://cpccbe.org/api/member/createmember', {
        username,
        job,
        subjob,
        address,
        city,
        pin,
        fromYear,
        toYear,
        position,
        imageUrl,
      }); // Adjust based on your actual data structure

      fetchmember(); // Refresh member list after adding
      handleCloseModal(); // Close modal after adding
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  // Handle updating a member
  const handleUpdatemember = async () => {
    try {
      await axios.patch(`https://cpccbe.org/api/member/updateMember/${editingmemberId}`, {
        username,
        job,
        subjob,
        address,
        city,
        pin,
        fromYear,
        toYear,
        position,
        imageUrl
      }); // Adjust endpoint as necessary
      fetchmember(); // Refresh member list after updating
      handleCloseModal(); // Close modal after updating
    } catch (error) {
      console.error('Error updating member:', error);
    }
  };

  // Handle deleting a member
  const handleDeletemember = async (memberId) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await axios.delete(`https://cpccbe.org/api/member/deletemember/${memberId}`); // Adjust endpoint as necessary
        fetchmember(); // Refresh member list after deletion
      } catch (error) {
        console.error('Error deleting member:', error);
      }
    }
  };

  const handleViewmember = (member) => {
    // Open a modal or set state to display member details
    setSelectedMember(member); // Assuming you have state to hold selected member data
    setViewModalOpen(true); // Open the modal for viewing member details
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - member.length) : 0;

  // Apply sorting and filtering
  const filteredmember = applySortFilter(member, getComparator(order, orderBy), filterName, roleFilter);

  const ismemberNotFound = filteredmember.length === 0;

  return (
    <Page title="Member">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Member
          </Typography>
          <Button variant="contained" onClick={handleOpenModal} startIcon={<Iconify icon="eva:plus-fill" />}>
            New Member
          </Button>
        </Stack>

        <Card>
          <MembersListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            roleFilter={roleFilter}
            onRoleFilterChange={(e) => setRoleFilter(e.target.value)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <MembersListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={member.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredmember.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id, email, role } = row; // Adjust based on your actual data structure
                    const isItemSelected = selected.indexOf(email) !== -1;

                    return (
                      <TableRow
                        hover
                        key={_id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell align="left">
                          <IconButton onClick={() => handleViewmember(row)} aria-label="view member">
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">{row.username}</TableCell>
                        <TableCell align="left">{row.job}</TableCell>
                        <TableCell align="left">{row.subjob}</TableCell>
                        <TableCell align="left">{row.address}</TableCell>
                        <TableCell align="left">{row.city}</TableCell>
                        <TableCell align="left">{row.pin}</TableCell>
                        <TableCell align="left">{row.fromYear}</TableCell>
                        <TableCell align="left">{row.toYear}</TableCell>
                        <TableCell align="left">{row.position}</TableCell>
                        <TableCell align="left">{row.imageUrl}</TableCell>
                      

                        <TableCell align="left">
                          <MembersMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDeletemember(row._id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {ismemberNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={member.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Modal for Editing member */}
        <Dialog open={openModal} onClose={handleCloseModal}>
          <DialogTitle>{editingmemberId ? 'Edit member' : 'Add New member'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="username"
              label="User Name"
              type="text"
              fullWidth
              value={username}
              onChange={(e) => setUserName(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="job"
              label="Job"
              type="text"
              fullWidth
              value={job}
              onChange={(e) => setJob(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="subjob"
              label="Sub Job"
              type="text"
              fullWidth
              value={subjob}
              onChange={(e) => setSubJob(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="address"
              label="Address"
              type="text"
              fullWidth
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="city"
              label="City"
              type="text"
              fullWidth
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="pin"
              label="Pin"
              type="text"
              fullWidth
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              id="fromyear"
              label="From Year"
              type="text"
              fullWidth
              value={fromYear}
              onChange={(e) => setFromYear(e.target.value)}
            />
            <TextField
              margin="dense"
              id="toyear"
              label="To Year"
              type="text"
              fullWidth
              value={toYear}
              onChange={(e) => setToYear(e.target.value)}
            />
            <TextField
              margin="dense"
              id="position"
              label="Position"
              type="text"
              fullWidth
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
            <TextField
              margin="dense"
              id="imageUrl"
              label="Image URL"
              type="text"
              fullWidth
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            <Divider sx={{ my: 2 }} />
            <Paper sx={{ p: 2, mt: 2, bgcolor: "#fff3e0", border: "1px solid #ffcc80" }}>
  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
    📌 Image Guidelines
  </Typography>

  <Typography variant="body2">
    • Upload square image (1:1 ratio) <br />
    • Recommended size: <b>800 × 800 px</b> <br />
    • Keep face centered (head & shoulders) <br />
    • Avoid extra background space <br />
    • Supported: Google Drive link or image URL
  </Typography>
</Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            {editingmemberId ? (
              <Button onClick={handleUpdatemember}>Update member</Button>
            ) : (
              <Button onClick={handleAddmember}>Add member</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
          <DialogTitle>Member Details</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="username"
              label="User Name"
              type="text"
              fullWidth
              value={selectedmember?.username}
              disabled
            />
            <TextField margin="dense" id="job" label="Job" type="text" fullWidth value={selectedmember?.job} disabled />
            <TextField
              margin="dense"
              id="subjob"
              label="Sub Job"
              type="text"
              fullWidth
              value={selectedmember?.subjob}
              disabled
            />
            <TextField
              margin="dense"
              id="address"
              label="Address"
              type="text"
              fullWidth
              value={selectedmember?.address}
              disabled
            />
            <TextField
              margin="dense"
              id="city"
              label="City"
              type="text"
              fullWidth
              value={selectedmember?.city}
              disabled
            />
            <TextField margin="dense" id="pin" label="Pin" type="text" fullWidth value={selectedmember?.pin} disabled />
            <TextField
              margin="dense"
              id="fromyear"
              label="From Year"
              type="text"
              fullWidth
              value={selectedmember?.fromYear}
              disabled
            />
            <TextField
              margin="dense"
              id="toyear"
              label="To Year"
              type="text"
              fullWidth
              value={selectedmember?.toYear}
              disabled
            />
            <TextField
              margin="dense"
              id="position"
              label="Position"
              type="text"
              fullWidth
              value={selectedmember?.position}
              disabled
            />
            <TextField
              margin="dense"
              id="imageUrl"
              label="Image URL"
              type="text"
              fullWidth
              value={selectedmember?.imageUrl}
              disabled
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
